import {
    AncillaryBaggageInfo,
    JourneyMapType,
    MealInfo,
    SegmentSeatData,
    SelectedAncillaryType
} from '@/src/types/itinerary'
import getItineraryEndpointPath from '@/src/utils/rtkQuery/getItineraryEndpointPath'
import setCommonHeaders from '@/src/utils/rtkQuery/setCommonHeaders'
import transformAddonsData from '@/src/utils/rtkQuery/transformAddonsData'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

interface AddonsQueryResponse {
    journeyAncillaries: {
        baggageAncillaries: {
            availableSegments: { from: string; to: string; key: string }[]
            segmentWiseAncillaryData: {
                [key: string]: AncillaryBaggageInfo[]
            }
        }
        mealAncillaries: {
            availableSegments: { from: string; to: string; key: string }[]
            segmentWiseAncillaryData: {
                [key: string]: MealInfo[]
            }
        }
        seatAncillaries: {
            availableSegments: { from: string; to: string; key: string }[]
            segmentWiseAncillaryData: {
                [key: string]: SegmentSeatData
            }
        }
    }
    ancillaryStatus: {
        isBaggageAvailable: boolean
        isMealAvailable: boolean
        isSeatAvailable: boolean
    }
    selectedAncillaries: SelectedAncillaryType[]
    journeyMap: JourneyMapType
    isAncillaryUpdated: boolean
}

export const addonsApi = createApi({
    reducerPath: 'addonsApi',
    baseQuery: fetchBaseQuery({
        prepareHeaders: (headers, api) => {
            return setCommonHeaders(headers)
        }
    }),

    tagTypes: ['Addons'],
    // Keep unused cached data for 10 minutes (600 seconds)
    keepUnusedDataFor: 600,

    endpoints: builder => ({
        // ========== QUERIES (READ DATA) ==========

        // Main addons data query
        getAddonsData: builder.query<AddonsQueryResponse, { itineraryId: string }>({
            query: ({ itineraryId }) =>
                getItineraryEndpointPath(
                    itineraryId,
                    `/itin/v2/${itineraryId}/ancillaries/fetch?includeBaggage=true&includeMeal=true&includeSeat=true`
                ),
            transformResponse: transformAddonsData,
            providesTags: (result, error, { itineraryId }) => [{ type: 'Addons', id: itineraryId }]
        }),

        // ========== MUTATIONS (WRITE DATA) ==========

        // Update addons data mutation
        updateAddonsData: builder.mutation({
            query: ({ itineraryId, payload }) => ({
                url: getItineraryEndpointPath(itineraryId, `/itin/v1/${itineraryId}/ancillaries/update`),
                method: 'PUT',
                body: payload
            })
        })
    })
})

// Export hooks for usage in components
export const { useGetAddonsDataQuery, useUpdateAddonsDataMutation } = addonsApi

// Export the API slice for store configuration
export default addonsApi
